import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { differenceInSeconds } from 'date-fns';
import {
  isAConversationRoute,
  isAInboxViewRoute,
} from 'dashboard/helper/routeHelpers';

class ReconnectService {
  constructor(store, router, connector = null) {
    this.store = store;
    this.router = router;
    this.disconnectTime = null;
    this.connector = connector;
    this.resumeTimer = null;
    this.syncPromise = null;
    this.announceReconnect = false;
    this.disposed = false;

    this.setupEventListeners();
  }

  disconnect = () => {
    this.disposed = true;
    clearTimeout(this.resumeTimer);
    this.removeEventListeners();
  };

  setupEventListeners = () => {
    window.addEventListener('online', this.handleOnlineEvent);
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('pagehide', this.markSuspended);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('freeze', this.markSuspended);
    document.addEventListener('resume', this.handleOnlineEvent);
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.on(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  removeEventListeners = () => {
    window.removeEventListener('online', this.handleOnlineEvent);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('pagehide', this.markSuspended);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    document.removeEventListener('freeze', this.markSuspended);
    document.removeEventListener('resume', this.handleOnlineEvent);
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.off(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  getSecondsSinceDisconnect = () =>
    this.disconnectTime
      ? Math.max(differenceInSeconds(new Date(), this.disconnectTime), 0)
      : 0;

  markSuspended = () => {
    this.disconnectTime ||= new Date();
    this.setConversationLastMessageId();
  };

  handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') this.markSuspended();
    else this.handleOnlineEvent();
  };

  handlePageShow = event => {
    if (event.persisted || this.disconnectTime) this.handleOnlineEvent();
  };

  handleOnlineEvent = () => {
    if (this.disposed || document.hidden || !navigator.onLine) return;
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(this.restoreVisibleConnection, 250);
  };

  restoreVisibleConnection = () => {
    if (this.disposed || document.hidden || !navigator.onLine) return;
    const consumer = this.connector?.consumer;
    const connection = consumer?.connection;
    const stale = connection?.monitor?.connectionIsStale();
    if (connection?.isOpen() && stale) {
      connection.reopen();
      this.resumeTimer = setTimeout(this.restoreVisibleConnection, 1000);
      return;
    }
    consumer?.ensureActiveConnection();
    // Actual disconnections are synchronized by the connector's reconnect event.
    if (!connection || (connection.isOpen() && !stale)) {
      this.synchronize(false);
    } else {
      // A suspended page may miss the socket's disconnect callback entirely.
      this.resumeTimer = setTimeout(this.restoreVisibleConnection, 1000);
    }
  };

  fetchConversations = async () => {
    await this.store.dispatch('fetchAllConversations', {
      requestParams: { page: 1, updatedWithin: null },
      preservePagination: true,
      throwOnError: true,
    });
  };

  fetchFilteredOrSavedConversations = async queryData => {
    await this.store.dispatch('fetchFilteredConversations', {
      queryData,
      page: 1,
      preservePagination: true,
      throwOnError: true,
    });
  };

  fetchConversationsOnReconnect = async () => {
    const {
      getAppliedConversationFiltersQuery,
      'customViews/getActiveConversationFolder': activeFolder,
    } = this.store.getters;
    const query = getAppliedConversationFiltersQuery?.payload?.length
      ? getAppliedConversationFiltersQuery
      : activeFolder?.query;
    if (query) {
      await this.fetchFilteredOrSavedConversations(query);
    } else {
      await this.fetchConversations();
    }
  };

  fetchConversationMessagesOnReconnect = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('syncActiveConversationMessages', {
        conversationId: Number(conversationId),
        throwOnError: true,
      });
    }
  };

  fetchNotificationsOnReconnect = async filter => {
    await this.store.dispatch('notifications/index', { ...filter, page: 1 });
  };

  revalidateCaches = async () => {
    const { label, inbox, team } = await this.store.dispatch(
      'accounts/getCacheKeys'
    );
    await Promise.all([
      this.store.dispatch('labels/revalidate', { newKey: label }),
      this.store.dispatch('inboxes/revalidate', { newKey: inbox }),
      this.store.dispatch('teams/revalidate', { newKey: team }),
    ]);
  };

  handleRouteSpecificFetch = async () => {
    const currentRoute = this.router.currentRoute.value.name;
    if (isAConversationRoute(currentRoute, true)) {
      await this.fetchConversationsOnReconnect();
      await this.fetchConversationMessagesOnReconnect();
    } else if (isAInboxViewRoute(currentRoute, true)) {
      await this.fetchNotificationsOnReconnect(
        this.store.getters['notifications/getNotificationFilters']
      );
    }
  };

  setConversationLastMessageId = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('setConversationLastMessageId', {
        conversationId: Number(conversationId),
      });
    }
  };

  onDisconnect = () => {
    this.disconnectTime ||= new Date();
    this.setConversationLastMessageId();
  };

  onReconnect = () => this.synchronize(true);

  synchronize = announce => {
    if (this.disposed) return Promise.resolve(false);
    this.announceReconnect ||= announce;
    if (this.syncPromise) return this.syncPromise;
    this.syncPromise = (async () => {
      try {
        await this.handleRouteSpecificFetch();
        await this.revalidateCaches();
        if (this.disposed) return false;
        clearTimeout(this.resumeTimer);
        this.disconnectTime = null;
        if (this.announceReconnect) {
          emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED);
          this.announceReconnect = false;
        }
        return true;
      } catch (_) {
        if (!this.disposed) {
          this.announceReconnect = true;
          emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
          clearTimeout(this.resumeTimer);
          this.resumeTimer = setTimeout(this.restoreVisibleConnection, 5000);
        }
        return false;
      } finally {
        this.syncPromise = null;
      }
    })();
    return this.syncPromise;
  };
}

export default ReconnectService;
